import { Card } from '@/components/ui/Card'
import { DataTable, type DataColumn } from '@/components/ui/DataTable'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { SectionHeader } from '@/components/ui/SectionHeader'
import { TableSkeleton } from '@/components/ui/Skeleton'
import { fmtInt, fmtMoney, fmtPct } from '@/lib/format'

/** One line of GET /accounting/by-model (models, providers, total). */
export interface PnlLine {
  model_code?: string
  provider?: string
  kind?: string | null
  generations: number
  paid_generations: number
  credits: string
  revenue_rub: string
  cost_usd: string
  cost_rub: string
  margin_rub: string
  /** null («—») while the revenue is below 1 ₽ */
  margin_pct: string | null
  revenue_per_generation_rub: string | null
  cost_per_generation_rub: string | null
  margin_per_generation_rub: string | null
}

export interface ProductLine {
  product_type: string
  product_ref: string
  tier: string | null
  payments: number
  refunded: number
  cash_in_rub: string
  access_revenue_rub: string
}

export interface ByModel {
  period_start: string
  period_end: string
  models: PnlLine[]
  providers: PnlLine[]
  products: ProductLine[]
  total: PnlLine
}

export interface ModelPnlTableProps {
  data?: ByModel
  error?: unknown
  onRetry?: () => void
}

const pct = (v: string | null) => (v === null ? '—' : fmtPct(v))
const money = (v: string | null) => (v === null ? '—' : fmtMoney(v))

function lineColumns(first: DataColumn<PnlLine>): DataColumn<PnlLine>[] {
  return [
    first,
    { id: 'gens', header: 'Генераций', type: 'number', mobile: 'meta', cell: (r) => fmtInt(r.generations) },
    {
      id: 'paid',
      header: 'Платных',
      type: 'number',
      mobile: 'hidden',
      cell: (r) => fmtInt(r.paid_generations),
    },
    { id: 'rev', header: 'Выручка', type: 'money', unit: '₽', mobile: 'hidden', cell: (r) => fmtMoney(r.revenue_rub) },
    { id: 'cost', header: 'Себестоимость', type: 'money', unit: '₽', mobile: 'hidden', cell: (r) => fmtMoney(r.cost_rub) },
    { id: 'margin', header: 'Маржа', type: 'money', unit: '₽', mobile: 'amount', cell: (r) => fmtMoney(r.margin_rub) },
    { id: 'pct', header: 'Маржа, %', type: 'percent', mobile: 'meta', cell: (r) => pct(r.margin_pct) },
    { id: 'rpg', header: 'Выручка на генерацию', type: 'money', unit: '₽', mobile: 'hidden', cell: (r) => money(r.revenue_per_generation_rub) },
    { id: 'cpg', header: 'Себестоимость на генерацию', type: 'money', unit: '₽', mobile: 'hidden', cell: (r) => money(r.cost_per_generation_rub) },
    { id: 'mpg', header: 'Маржа на генерацию', type: 'money', unit: '₽', mobile: 'hidden', cell: (r) => money(r.margin_per_generation_rub) },
  ]
}

const MODEL_COLUMNS = lineColumns({
  id: 'model',
  header: 'Модель',
  type: 'name',
  mobile: 'title',
  cell: (r) => (
    <span className="flex flex-col">
      <span className="text-ink-1">{r.model_code}</span>
      <span className="text-caption text-ink-3">{r.provider}</span>
    </span>
  ),
})
const PROVIDER_COLUMNS = lineColumns({ id: 'provider', header: 'Провайдер', type: 'name', mobile: 'title', cell: (r) => r.provider })

const PRODUCT_COLUMNS: DataColumn<ProductLine>[] = [
  {
    id: 'product',
    header: 'Продукт',
    type: 'name',
    mobile: 'title',
    cell: (p) => `${p.product_type === 'subscription' ? 'Подписка' : 'Пакет'} ${p.product_ref}`,
  },
  { id: 'tier', header: 'Тариф', mobile: 'meta', cell: (p) => p.tier ?? '—' },
  { id: 'payments', header: 'Оплат', type: 'number', mobile: 'meta', cell: (p) => fmtInt(p.payments) },
  { id: 'refunded', header: 'Возвращено', type: 'number', mobile: 'hidden', cell: (p) => fmtInt(p.refunded) },
  { id: 'cash', header: 'Поступления', type: 'money', unit: '₽', mobile: 'amount', cell: (p) => fmtMoney(p.cash_in_rub) },
  { id: 'access', header: 'Выручка доступа', type: 'money', unit: '₽', mobile: 'hidden', cell: (p) => fmtMoney(p.access_revenue_rub) },
]

/**
 * «По моделям» (docs/accounting.md): the margin of a generation is the revenue recognized for it
 * (the ⚡ it spent at the user's average price; 0 for free ⚡) minus its provider cost at the rate
 * of the day it ran. The admin Dashboard shows the same margin. Products are payments of the
 * period; spent ⚡ cannot be traced to the package that brought them, so consumption lives here.
 */
export function ModelPnlTable({ data, error, onRetry }: ModelPnlTableProps) {
  if (data === undefined) {
    return error ? (
      <Card>
        <ErrorState title="Не удалось загрузить P&L по моделям" error={error} onRetry={onRetry} />
      </Card>
    ) : (
      <TableSkeleton cols={6} />
    )
  }
  const t = data.total
  return (
    <div className="flex flex-col gap-4">
      <Card>
        <div className="grid grid-cols-1 gap-x-6 gap-y-2 text-body sm:grid-cols-2 xl:grid-cols-4">
          <KV k="Генераций" v={`${fmtInt(t.generations)}, платных ${fmtInt(t.paid_generations)}`} />
          <KV k="Выручка генераций" v={fmtMoney(t.revenue_rub, 'RUB')} />
          <KV k="Себестоимость" v={fmtMoney(t.cost_rub, 'RUB')} />
          <KV k="Маржа" v={`${fmtMoney(t.margin_rub, 'RUB')}, ${pct(t.margin_pct)}`} />
          <KV k="Выручка на генерацию" v={t.revenue_per_generation_rub === null ? '—' : fmtMoney(t.revenue_per_generation_rub, 'RUB')} />
          <KV k="Себестоимость на генерацию" v={t.cost_per_generation_rub === null ? '—' : fmtMoney(t.cost_per_generation_rub, 'RUB')} />
          <KV k="Маржа на генерацию" v={t.margin_per_generation_rub === null ? '—' : fmtMoney(t.margin_per_generation_rub, 'RUB')} />
        </div>
      </Card>
      <section>
        <SectionHeader as="h3" title="По моделям" />
        {data.models.length === 0 ? (
          <EmptyState title="Генераций за период нет" text="Строки появятся после первой завершённой генерации." />
        ) : (
          <DataTable label="P&L по моделям" rows={data.models} columns={MODEL_COLUMNS} getRowId={(r) => `${r.model_code}:${r.provider}`} />
        )}
      </section>
      {data.providers.length > 0 && (
        <section>
          <SectionHeader as="h3" title="По провайдерам" />
          <DataTable label="P&L по провайдерам" rows={data.providers} columns={PROVIDER_COLUMNS} getRowId={(r) => String(r.provider)} />
        </section>
      )}
      <section>
        <SectionHeader as="h3" title="По продуктам" description="Оплаты периода по пакетам и подпискам, в том числе возвращённые." />
        {data.products.length === 0 ? (
          <EmptyState title="Оплат за период нет" text="Строки появятся после первой оплаты." />
        ) : (
          <DataTable
            label="Поступления по продуктам"
            rows={data.products}
            columns={PRODUCT_COLUMNS}
            getRowId={(p) => `${p.product_type}:${p.product_ref}:${p.tier ?? ''}`}
          />
        )}
      </section>
    </div>
  )
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="min-w-0 text-ink-2">{k}</span>
      <span className="tnum shrink-0 text-right text-ink-1">{v}</span>
    </div>
  )
}
